import { z } from 'zod';
import { allItemsResponse, optionalCuid, decimalString } from './common';

/**
 * Schemas for the per-project Lighting line items.
 *
 * Path: `/projects/:projectId/lighting`
 *
 * Snapshot semantics:
 * - When created `from-catalog`, the line item copies the catalog template
 *   fields and restores the linked Lighting product, quantity, coefficient,
 *   unit price, and currency when those were captured in catalog attributes.
 * - `catalogItemId` is recorded for traceability and is **immutable** once set.
 * - Subsequent edits to the catalog item do NOT propagate to existing lines.
 *
 * Worker access:
 * - Workers may LIST/GET lighting only on projects they are assigned to.
 * - Workers see no `unitPriceNet`/`currency` (financials hidden).
 * - Workers may not mutate (`PROJECT_ITEMS_CREATE/UPDATE/DELETE`).
 *
 * Status locking:
 * - Mutations are forbidden when the parent project is in a terminal status
 *   (`ACCEPTED` or `REJECTED`).
 */

const positiveDecimal = decimalString.refine((v) => parseFloat(v) > 0, {
  message: 'Must be greater than 0',
});

const positiveIntegerDecimal = decimalString.refine(
  (v) => Number.isInteger(Number(v)) && Number(v) > 0,
  {
    message: 'Must be a positive integer',
  },
);

const nonNegativeDecimal = decimalString.refine((v) => parseFloat(v) >= 0, {
  message: 'Must be ≥ 0',
});

const optionalDimMm = z.number().int().min(0).max(100_000).optional();

const lightingCurrencyEnum = z.enum(['RON', 'EUR', 'USD']);
export type LightingCurrencyValue = z.infer<typeof lightingCurrencyEnum>;

const optionalTrim = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v === '' ? undefined : v));

export const lightingListItem = z.object({
  id: z.string(),
  projectId: z.string(),
  roomId: z.string().cuid().nullable(),
  areaId: z.string().cuid().nullable(),
  catalogItemId: z.string().nullable(),
  name: z.string(),
  sku: z.string().nullable(),
  wattage: z.number().int().nullable(),
  colorTempK: z.number().int().nullable(),
  lengthMm: z.number().int().nullable(),
  quantity: decimalString,
  lengthM: decimalString.optional(),
  coefficient: decimalString,
  unitPriceNet: decimalString.optional(),
  /**
   * Unit price WITHOUT the markup: MANUAL rows repeat `unitPriceNet`; AUTO rows
   * report the engine's catalog-cost sum (`subtotalBaseNet`), absent until a
   * breakdown stored before that field existed is recomputed. Absent when
   * finances are hidden.
   */
  unitPriceBaseNet: decimalString.optional(),
  /**
   * Unit price WITH the markup: `unitPriceNet` times the row coefficient. On
   * AUTO rows the markups already live inside `unitPriceNet`, so the two agree
   * there by construction. Absent when finances are hidden.
   */
  unitPriceFinalNet: decimalString.optional(),
  currency: lightingCurrencyEnum.optional(),
  totalNet: decimalString.optional(),
  pricingMode: z.enum(['MANUAL', 'AUTO']),
  lightingCatalogId: z.string().nullable(),
  lightingCatalogName: z.string().nullable(),
  lightingCatalogSku: z.string().nullable(),
  lightingCatalogManufacturer: z.string().nullable(),
  computedBreakdown: z.unknown().nullable().optional(),
  pricingError: z.string().optional(),
  orderIndex: z.number().int(),
  notes: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type LightingListItem = z.infer<typeof lightingListItem>;

export const lightingDetails = lightingListItem;
export type LightingDetails = z.infer<typeof lightingDetails>;

export const listLightingResponse = allItemsResponse(lightingListItem);
export type ListLightingResponse = z.infer<typeof listLightingResponse>;

// ----- Manual create, optionally linked to the dedicated Lighting catalog -----
export const createLightingRequest = z.object({
  roomId: optionalCuid(),
  areaId: optionalCuid(),
  name: z.string().trim().min(1).max(200),
  sku: optionalTrim(50),
  wattage: z.coerce.number().int().min(0).max(100000).optional(),
  colorTempK: z.coerce.number().int().min(0).max(20000).optional(),
  lengthMm: optionalDimMm,
  quantity: positiveIntegerDecimal.default('1'),
  lengthM: nonNegativeDecimal.optional(),
  coefficient: positiveDecimal.default('1.8'),
  unitPriceNet: nonNegativeDecimal.default('0.00'),
  currency: lightingCurrencyEnum.default('RON'),
  notes: optionalTrim(2000),
  pricingMode: z.enum(['MANUAL', 'AUTO']).optional(),
  lightingCatalogId: z.string().cuid().optional().nullable(),
});
export type CreateLightingRequest = z.infer<typeof createLightingRequest>;

// ----- Create from a CatalogItem (snapshot) -----
//
// Catalog lighting templates can carry product/quantity/coefficient snapshots.
// Request-level values remain optional overrides for older templates.
export const createLightingFromCatalogRequest = z.object({
  catalogItemId: z.string().min(1),
  roomId: optionalCuid(),
  areaId: optionalCuid(),
  unitPriceNet: nonNegativeDecimal.optional(),
  currency: lightingCurrencyEnum.optional(),
  quantity: positiveIntegerDecimal.optional(),
  coefficient: positiveDecimal.optional(),
  notes: optionalTrim(2000),
  overrides: z
    .object({
      name: z.string().trim().min(1).max(200).optional(),
      sku: optionalTrim(50),
      wattage: z.coerce.number().int().min(0).max(100000).optional(),
      colorTempK: z.coerce.number().int().min(0).max(20000).optional(),
      lengthMm: optionalDimMm,
      lightingCatalogId: z.string().cuid().optional().nullable(),
    })
    .optional(),
});
export type CreateLightingFromCatalogRequest = z.infer<typeof createLightingFromCatalogRequest>;

// ----- Update (partial) -----
//
// `catalogItemId` is intentionally not patchable — the snapshot link is
// permanent. `projectId` is taken from the URL.
export const updateLightingRequest = z
  .object({
    roomId: z.string().cuid().nullable(),
    areaId: z.string().cuid().nullable(),
    name: z.string().trim().min(1).max(200),
    sku: optionalTrim(50),
    wattage: z.coerce.number().int().min(0).max(100000).optional(),
    colorTempK: z.coerce.number().int().min(0).max(20000).optional(),
    lengthMm: optionalDimMm,
    quantity: positiveIntegerDecimal,
    lengthM: nonNegativeDecimal,
    coefficient: positiveDecimal,
    unitPriceNet: nonNegativeDecimal,
    currency: lightingCurrencyEnum,
    orderIndex: z.number().int().min(0),
    notes: optionalTrim(2000),
    pricingMode: z.enum(['MANUAL', 'AUTO']),
    lightingCatalogId: z.string().cuid().nullable(),
  })
  .partial();
export type UpdateLightingRequest = z.infer<typeof updateLightingRequest>;
